import React from "react";
import { Zap, ChevronDown, X } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

/**
 * Reusable "Actions ▾" bulk-action dropdown for any selectable list (Clients,
 * Projects, Team, etc.). Pass the current selection count and a list of
 * `{ key, label, icon, danger, onClick }` actions — the trigger is disabled
 * until at least one row is selected. A "Clear selection" link appears next
 * to the trigger once something is selected.
 *
 * Usage:
 *   <BulkActionsBar
 *     count={selected.size}
 *     actions={[{ key: "delete", label: "Delete selected", icon: Trash2, danger: true, onClick: bulkDelete }]}
 *     onClear={() => setSelected(new Set())}
 *     testidPrefix="clients"
 *   />
 */
export function BulkActionsBar({ count, actions, onClear, testidPrefix = "bulk" }) {
  const disabled = count === 0;
  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            disabled={disabled}
            data-testid={`${testidPrefix}-actions-btn`}
            title={disabled ? "Select rows to enable actions" : `${count} selected`}
            className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Zap size={14} /> Actions {count > 0 && <span className="text-xs text-coral">({count})</span>} <ChevronDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-white min-w-[200px]">
          {actions.map((a, i) => (
            <React.Fragment key={a.key}>
              {a.danger && i > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={a.onClick}
                data-testid={`${testidPrefix}-action-${a.key}`}
                className={a.danger ? "text-red-600 focus:text-red-700 focus:bg-red-50" : ""}
              >
                {a.icon && <a.icon size={14} />} {a.label}
              </DropdownMenuItem>
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {count > 0 && (
        <button onClick={onClear} data-testid={`${testidPrefix}-clear-selection`}
                className="text-xs text-[#6a736f] hover:text-coral inline-flex items-center gap-1">
          <X size={12} /> Clear selection
        </button>
      )}
    </div>
  );
}
